/**
 * Language code normalization utilities.
 *
 * The backend stores language codes in mixed formats: 'es_ES', 'en-US', 'fr', 'fil_PH'.
 * The frontend locale files use 2-letter primary subtags: 'es.json', 'en.json', 'fr.json'.
 *
 * These pure functions act as the single adapter between both worlds.
 * They have zero dependencies — no Vue, no i18n, no store.
 */

/**
 * Extract the primary language subtag from any locale code.
 *
 * Examples:
 *   'es_ES'  → 'es'
 *   'en-US'  → 'en'
 *   'fr'     → 'fr'
 *   'fil_PH' → 'fil'
 *   'es-419' → 'es'
 *   null     → ''
 *
 * @param {string|null|undefined} code - Raw language code from any source
 * @returns {string} Lowercase primary subtag, or '' for falsy input
 */
function normalizeLocale(code) {
	if (!code || typeof code !== 'string') return '';
	return code.split(/[-_]/)[0].toLowerCase();
}

/**
 * Compare two language codes by their primary subtag.
 *
 * Examples:
 *   ('es_ES', 'es')    → true
 *   ('en-US', 'en_GB') → true   (same language, different region)
 *   ('es', 'fr')       → false
 *
 * @param {string|null|undefined} codeA - First language code
 * @param {string|null|undefined} codeB - Second language code
 * @returns {boolean} True if primary subtags match
 */
function localesMatch(codeA, codeB) {
	return normalizeLocale(codeA) === normalizeLocale(codeB);
}

/**
 * Find a matching language entry from an array of objects with a `code` property.
 * Tries exact match first, then normalized (primary subtag) match.
 *
 * Examples:
 *   findLanguageByCode([{ code: 'es_ES' }], 'es')    → { code: 'es_ES' }
 *   findLanguageByCode([{ code: 'es_ES' }], 'es_ES') → { code: 'es_ES' }  (exact)
 *   findLanguageByCode([{ code: 'en_GB' }], 'fr')    → undefined
 *   findLanguageByCode([], 'es')                     → undefined
 *
 * @param {Array<{code: string}>} languages - Available language entries
 * @param {string|null|undefined} targetCode - Code to search for
 * @returns {{code: string}|undefined} Matching entry, or undefined if not found
 */
function findLanguageByCode(languages, targetCode) {
	if (!Array.isArray(languages) || !targetCode) return undefined;
	return (
		languages.find(lang => lang.code === targetCode) ||
		languages.find(lang => localesMatch(lang.code, targetCode))
	);
}

export { normalizeLocale, localesMatch, findLanguageByCode };
